import { useTranslation } from 'react-i18next';
import {
  Users, Calendar, Building2, BookOpen,
  MessageSquare, Video, Leaf, Apple,
  UtensilsCrossed, Sparkles, Briefcase, Newspaper,
  Zap, Star, Lock, Crown, Bookmark,
  Bell, Send
} from 'lucide-react';
import { Meta } from '../components/Meta';

export function Features() {
  const { t } = useTranslation();
  const features = [
    { icon: Users, titleKey: 'practitioners', descKey: 'practitionersDesc', detailsKeys: ['practitionerProfiles', 'verifiedCredentials', 'directMessagingDetail', 'corporateWellnessFilter', 'sessionsTypes'] },
    { icon: Calendar, titleKey: 'eventsWorkshops', descKey: 'eventsWorkshopsDesc', detailsKeys: ['eventCreation', 'ticketSales', 'attendeeMgmt', 'eventDiscovery', 'calendarIntegration'] },
    { icon: Building2, titleKey: 'venues', descKey: 'venuesDesc', detailsKeys: ['venueListings', 'directMessagingDetail', 'amenitiesShowcase', 'virtualTours'] },
    { icon: BookOpen, titleKey: 'onlineCourses', descKey: 'onlineCoursesDesc', detailsKeys: ['videoLearning', 'progressTracking', 'certificates', 'interactiveContent'] },
    { icon: MessageSquare, titleKey: 'communities', descKey: 'communitiesDesc', detailsKeys: ['publicPrivateGroups', 'discussionForums', 'mediaSharing', 'communityEvents', 'verifiedCommunities', 'memberMgmt'] },
    { icon: Video, titleKey: 'liveStreaming', descKey: 'liveStreamingDesc', detailsKeys: ['liveSessions', 'interactiveChat', 'scheduleNotifications', 'multiPlatformStreaming'] },
    { icon: Leaf, titleKey: 'agriTools', descKey: 'agriToolsDesc', detailsKeys: ['fieldMgmt', 'taskTracking', 'teamCollaboration', 'harvestPlanning', 'weatherIntegration'] },
    { icon: Apple, titleKey: 'nutritionGuide', descKey: 'nutritionGuideDesc', detailsKeys: ['nutrientDatabase', 'dietaryInfo', 'supplementGuides', 'healthBenefits', 'foodSources'] },
    { icon: UtensilsCrossed, titleKey: 'recipeSharing', descKey: 'recipeSharingDesc', detailsKeys: ['recipeCreation', 'saveRecipes', 'dietaryFilters', 'stepInstructions', 'ingredientChecklists'] },
    { icon: Briefcase, titleKey: 'wellnessJobs', descKey: 'wellnessJobsDesc', detailsKeys: ['jobListings', 'applicationMgmt', 'companyProfiles', 'jobAlerts', 'industryCategories'] },
    { icon: Newspaper, titleKey: 'articlesBlog', descKey: 'articlesBlogDesc', detailsKeys: ['richTextEditor', 'categoryOrg', 'featuredArticles', 'authorProfiles'] },
    { icon: Send, titleKey: 'directMessaging', descKey: 'directMessagingDesc', detailsKeys: ['realtimeChat', 'groupConversations', 'mediaSharing', 'messageNotifications'] },
    { icon: Bookmark, titleKey: 'bookmarksSaved', descKey: 'bookmarksSavedDesc', detailsKeys: ['saveContentType', 'organizedCategory', 'quickAccess', 'syncDevices'] },
    { icon: Bell, titleKey: 'notifications', descKey: 'notificationsDesc', detailsKeys: ['pushNotifications', 'inAppAlerts', 'emailDigests', 'customizablePrefs'] }
  ];

  const premiumFeatures = [
    { icon: Crown, titleKey: 'proFeatures', descKey: 'proFeaturesDesc', benefitsKeys: ['proCreateListings', 'proHostEvents', 'proAnalytics', 'proPrioritySupport'] },
    { icon: Lock, titleKey: 'securityPrivacy', descKey: 'securityPrivacyDesc', benefitsKeys: ['securePayment', 'dataEncryption', 'gdprCompliance', 'securityAudits', 'privacyControls'] },
    { icon: Star, titleKey: 'supportCommunity', descKey: 'supportCommunityDesc', benefitsKeys: ['support247', 'communityGuidelines', 'helpDocs', 'featureRequests', 'regularUpdates'] }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3F7EE] to-white">
      <Meta 
        title={t('features.metaTitle')}
        description={t('features.metaDesc')}
      />

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-page mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-gelica text-content leading-tight mb-6">
            {t('features.title')}
          </h1>
          <p className="text-xl text-content/70 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-accent-base/10 rounded-lg">
                    <Icon className="h-6 w-6 text-accent-text" />
                  </div>
                  <h3 className="text-xl font-semibold text-content ml-4">
                    {t(`features.${feature.titleKey}`)}
                  </h3>
                </div>
                <p className="text-content/70 mb-4">
                  {t(`features.${feature.descKey}`)}
                </p>
                <ul className="space-y-2">
                  {feature.detailsKeys.map((key, i) => (
                    <li key={i} className="flex items-center text-content/70">
                      <span className="w-1.5 h-1.5 bg-accent-text rounded-full mr-2"></span>
                      {t(`features.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Premium Features */}
      <div className="bg-white py-24">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-gelica font-bold text-content mb-4">
              {t('features.proBenefits')}
            </h2>
            <p className="text-lg text-content/70">
              {t('features.proBenefitsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {premiumFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-accent-base/5 rounded-xl p-8"
                >
                  <div className="flex items-center mb-6">
                    <Icon className="h-8 w-8 text-accent-text" />
                    <h3 className="text-xl font-semibold text-content ml-4">
                      {t(`features.${feature.titleKey}`)}
                    </h3>
                  </div>
                  <p className="text-content/70 mb-6">
                    {t(`features.${feature.descKey}`)}
                  </p>
                  <ul className="space-y-3">
                    {feature.benefitsKeys.map((key, i) => (
                      <li key={i} className="flex items-center text-content/70">
                        <Zap className="h-4 w-4 text-accent-text mr-3 flex-shrink-0" />
                        {t(`features.${key}`)}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-gradient-to-b from-white to-[#F3F7EE]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-gelica font-bold text-content mb-6">
            {t('features.readyToStart')}
          </h2>
          <p className="text-xl text-content/70 mb-8">
            {t('features.readyToStartDesc')}
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-accent-text hover:bg-accent-text/90 transition-colors"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            {t('features.createAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}